// One component to render an article the way the paper displays it, with
// kind-specific chrome (red rule for notice, price kicker for offer, event
// time range).
//
// Interaction model:
//   - Whole card is clickable → toggles expanded state (reveals full body).
//   - Tiny reactions strip (👍 / 👎 / 💬) sits right below the title so
//     readers can gauge sentiment before diving in. Recommend and dislike
//     are mutually exclusive on the server; the UI mirrors that.
//   - The compose preview passes `dense` to suppress interactive chrome.

import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Post } from '../types/api';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatEventRange, offerValidity, timeAgo } from '../lib/format';
import { markdownToPlainText, renderMarkdown } from '../lib/markdown';
import { fireSignal, trackDwell } from '../lib/signals';

interface Props {
    post: Post;
    /** Preview mode — hide meta row + disable interaction. */
    dense?: boolean;
}

export function PostCard({ post, dense = false }: Props) {
    const api = useApi();
    const { authed, login } = useAuth();
    const cardRef = useRef<HTMLElement | null>(null);
    const [expanded,   setExpanded]     = useState(false);
    const [recommends, setRecommends]   = useState(post.like_count ?? 0);
    const [dislikes,   setDislikes]     = useState(post.dislike_count   ?? 0);
    const [mineRec,    setMineRec]      = useState(!!post.has_liked);
    const [mineDis,    setMineDis]      = useState(!!post.has_disliked);
    const commentCount                  = post.comment_count ?? 0;

    // Reader-paper dwell tracking (PP-1). Skipped in dense/preview mode
    // where the card isn't a real reading surface, and skipped for anon —
    // /api/reader-signals is authed-only and there's no reader-affinity to
    // update for a guest anyway.
    useEffect(() => {
        if (dense || !cardRef.current || !authed) return;
        return trackDwell(cardRef.current, api, post.id);
    }, [api, post.id, dense, authed]);

    const kind = post.post_type;
    let kicker = '';
    let sideBadge = '';
    if (kind === 'event') {
        kicker = formatEventRange(post.starts_at, post.ends_at);
    } else if (kind === 'offer') {
        if (post.price_text)  kicker    = post.price_text;
        if (post.valid_until) sideBadge = offerValidity(post.valid_until);
    } else if (kind === 'notice') {
        kicker = 'Notice';
    }

    const title = post.title || '(untitled)';
    const short = post.subhead || markdownToPlainText(post.body, 200);

    // Clicks on the card toggle expand. Clicks on nested links / buttons
    // shouldn't bubble up into a toggle — button handlers stopPropagation.
    function onCardClick(e: MouseEvent) {
        if (dense) return;
        const t = e.target as HTMLElement;
        if (t.closest('a') || t.closest('button') || t.tagName === 'IMG') return;
        setExpanded(v => !v);
    }

    async function toggleRecommend(e: MouseEvent) {
        e.stopPropagation();
        if (dense) return;
        if (!authed) { login(); return; }
        const nextMine = !mineRec;
        // Optimistic: flip locally + collapse any active dislike (server does
        // the same). Roll everything back on error.
        const hadDislike = mineDis;
        setMineRec(nextMine);
        setRecommends(n => n + (nextMine ? 1 : -1));
        if (nextMine && hadDislike) {
            setMineDis(false);
            setDislikes(n => Math.max(0, n - 1));
        }
        try {
            if (nextMine) await api.post(`/api/posts/${post.id}/recommend`);
            else          await api.del (`/api/posts/${post.id}/recommend`);
            if (nextMine) fireSignal(api, post.id, 'recommend');
        } catch {
            setMineRec(!nextMine);
            setRecommends(n => n + (nextMine ? -1 : 1));
            if (nextMine && hadDislike) {
                setMineDis(true);
                setDislikes(n => n + 1);
            }
        }
    }

    async function toggleDislike(e: MouseEvent) {
        e.stopPropagation();
        if (dense) return;
        if (!authed) { login(); return; }
        const nextMine = !mineDis;
        const hadRec = mineRec;
        setMineDis(nextMine);
        setDislikes(n => n + (nextMine ? 1 : -1));
        if (nextMine && hadRec) {
            setMineRec(false);
            setRecommends(n => Math.max(0, n - 1));
        }
        try {
            if (nextMine) await api.post(`/api/posts/${post.id}/dislike`);
            else          await api.del (`/api/posts/${post.id}/dislike`);
        } catch {
            setMineDis(!nextMine);
            setDislikes(n => n + (nextMine ? -1 : 1));
            if (nextMine && hadRec) {
                setMineRec(true);
                setRecommends(n => n + 1);
            }
        }
    }

    return (
        <article
            ref={cardRef}
            className={'post-card kind-' + kind + (expanded ? ' expanded' : '')}
            onClick={onCardClick}
            style={dense ? undefined : { cursor: 'pointer' }}
        >
            {kicker && <div className="art-kicker">{kicker}</div>}
            <h3>
                {dense ? title : (
                    <Link to={'/posts/' + post.id} className="post-title-link">{title}</Link>
                )}
            </h3>
            {!dense && (
                <div className="reactions-strip">
                    <button
                        type="button"
                        className={'react-btn' + (mineRec ? ' is-active' : '')}
                        onClick={toggleRecommend}
                        aria-pressed={mineRec}
                        title="Like"
                    >
                        <span className="react-emoji">👍</span>
                        <span className="react-count">{recommends}</span>
                    </button>
                    <button
                        type="button"
                        className={'react-btn' + (mineDis ? ' is-active-dis' : '')}
                        onClick={toggleDislike}
                        aria-pressed={mineDis}
                        title="Not for me"
                    >
                        <span className="react-emoji">👎</span>
                        <span className="react-count">{dislikes}</span>
                    </button>
                    <Link
                        to={'/posts/' + post.id + '#comments'}
                        className="react-btn"
                        onClick={e => e.stopPropagation()}
                        title="Comments"
                    >
                        <span className="react-emoji">💬</span>
                        <span className="react-count">{commentCount}</span>
                    </Link>
                </div>
            )}
            <div className="byline">
                {post.display_identity} · {timeAgo(post.created_at)}
                {sideBadge && <> · <span className="offer-badge">{sideBadge}</span></>}
                {post.topic && <> · <span className="topic-flair">{post.topic.replace(/_/g, ' ')}</span></>}
            </div>
            {post.media_url && <img className="article-img" src={post.media_url} alt="" loading="lazy" />}
            <p className="lede">{short}</p>
            {!dense && (
                <div className="article-meta">
                    <button type="button" className="expand-btn" onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}>
                        {expanded ? 'Show less ↑' : 'Read more →'}
                    </button>
                    {authed && (
                        <button
                            type="button"
                            className="expand-btn"
                            style={{ opacity: 0.6 }}
                            title="Report this post to OnHere staff"
                            onClick={async (e) => {
                                e.stopPropagation();
                                const reason = window.prompt('Report this post — why? (spam, illegal, harassment, misinformation, other)');
                                if (!reason) return;
                                try {
                                    await api.post(`/api/posts/${post.id}/flag`, { reason });
                                    alert('Reported. Staff will review it.');
                                } catch (err) {
                                    alert('Report failed: ' + (err as Error).message);
                                }
                            }}
                        >⚑</button>
                    )}
                </div>
            )}
            {expanded && !dense && (
                <div
                    className="article-full"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(post.body) }}
                />
            )}
        </article>
    );
}
